import React, { useEffect, useRef } from 'react'
import { observer } from 'mobx-react'
import { Button } from 'antd'
import { Link, useHistory } from 'react-router-dom'
import { useAccessOwner } from './access-owner-provider'

/** The most domains the screen lists. */
export const MAX_LISTED_DOMAINS = 8

const CENTERED: React.CSSProperties = { textAlign: 'center', padding: '15vh 16px 0' }
const TITLE: React.CSSProperties = { fontSize: 24, fontWeight: 'bold', margin: 0, outline: 'none' }

/**
 * Shown when the URL names a domain or organization the user cannot open. One message covers a
 * domain that does not exist and one the user may not open, so it cannot be used to probe for
 * either. It never shows the requested id or any error text, and it offers only the user's own
 * domains and dashboard. It replaces the page, so it interrupts (role alert) and takes focus on its
 * heading.
 */
export const AccessUnavailable = observer(() => {
    const history = useHistory()
    const listed = useAccessOwner().domains.slice(0, MAX_LISTED_DOMAINS)
    const heading = useRef<HTMLHeadingElement>(null)

    useEffect(() => {
        heading.current?.focus()
    }, [])

    return (
        <div role="alert" data-testid="access-unavailable" style={CENTERED}>
            <h1 ref={heading} tabIndex={-1} style={TITLE}>
                This page is not available
            </h1>
            <div style={{ fontSize: 16, margin: '8px 0 16px' }}>
                It may not exist, or you may not have access to it.
            </div>
            <Button type="primary" size="large" onClick={() => history.push('/')}>
                Go to my dashboard
            </Button>
            {listed.length > 0 && (
                <div data-testid="access-unavailable-domains" style={{ margin: '32px auto 0', maxWidth: 420 }}>
                    <h2 style={{ fontSize: 16, fontWeight: 'bold', margin: '0 0 8px' }}>Your domains</h2>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {listed.map((domain) => (
                            <li key={domain.id} style={{ padding: '4px 0' }}>
                                <Link to={`/domains/${domain.id}/dashboard`}>{domain.displayName || domain.name}</Link>
                                {domain.accountName ? <span> ({domain.accountName})</span> : null}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
})
